import { useCallback, useState } from 'react';
import { useHandleApiResponse, useToast } from '../../../../hooks';
import { useProcessUrlMutation } from '../../../../services';
import { FilePurpose, ProspectCategory, ProspectType } from '../../../../types';
import { isValidURL } from '../../../../utils';
import { AlertType, ButtonGroup, TextButton, TextInput } from '../../../shared';

const SHARE_MEDIA_LINK_ERROR_MSG = 'Failed to share media link. Please try again later.';

interface ShareMediaLinkProps {
  onBack: () => void;
  onSuccess: () => void;
  newCategory?: ProspectCategory;
  newType?: ProspectType;
}

const ShareMediaLink = ({ onBack, newCategory, newType, onSuccess }: ShareMediaLinkProps) => {
  const [linkInput, setLinkInput] = useState('');
  const [error, setError] = useState('');

  const [processUrl, { isLoading: isProcessingUrl }] = useProcessUrlMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const handleLinkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkInput(e.target.value);
    setError('');
  }, []);

  const handleShareMediaLink = useCallback(async () => {
    if (!linkInput || !!error || !newCategory || !newType) return;

    if (!isValidURL(linkInput)) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      const response = await processUrl({
        urls: [
          {
            url: linkInput,
            purpose: FilePurpose.PROSPECT_CREATION,
            prospectCategory: newCategory,
            prospectType: newType,
          },
        ],
      });
      handleApiResponse({
        response,
        errorMsg: SHARE_MEDIA_LINK_ERROR_MSG,
        hideSuccessToast: true, // Hide the success toast because we already show a success state.
        onSuccess,
      });
    } catch (error) {
      console.error(error);
      showToast({ message: SHARE_MEDIA_LINK_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [linkInput, error, processUrl, handleApiResponse, newCategory, newType, onSuccess, showToast]);

  return (
    <div className="flex flex-col gap-4">
      <TextInput placeholder="https://" value={linkInput} error={error} onChange={handleLinkChange} />
      <ButtonGroup>
        <TextButton onClick={handleShareMediaLink} loading={isProcessingUrl} text="Next" disabled={!linkInput} />
        <TextButton onClick={onBack} text="Back" />
      </ButtonGroup>
    </div>
  );
};

export default ShareMediaLink;
